import { useEffect, useState, useCallback } from 'react';
import { Capacitor } from '@capacitor/core';

export function useCapacitorKeyboard() {
  const [keyboardHeight, setKeyboardHeight] = useState(0);
  const [isKeyboardVisible, setIsKeyboardVisible] = useState(false);

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    let cancelled = false;

    async function setup() {
      try {
        const { Keyboard, KeyboardResize } = await import('@capacitor/keyboard');

        if (cancelled) return;

        // Set resize mode to 'none' -- we handle layout ourselves
        Keyboard.setResizeMode({ mode: KeyboardResize.None }).catch(() => {});

        const showHandle = await Keyboard.addListener('keyboardWillShow', (info) => {
          if (!cancelled) {
            setKeyboardHeight(info.keyboardHeight);
            setIsKeyboardVisible(true);
          }
        });

        const hideHandle = await Keyboard.addListener('keyboardWillHide', () => {
          if (!cancelled) {
            setKeyboardHeight(0);
            setIsKeyboardVisible(false);
          }
        });

        // Store cleanup references
        cleanupRef.showHandle = showHandle;
        cleanupRef.hideHandle = hideHandle;
      } catch {
        // @capacitor/keyboard not available (web or missing plugin)
      }
    }

    const cleanupRef: { showHandle?: { remove: () => void }; hideHandle?: { remove: () => void } } = {};

    setup();

    return () => {
      cancelled = true;
      cleanupRef.showHandle?.remove();
      cleanupRef.hideHandle?.remove();
    };
  }, []);

  const hideKeyboard = useCallback(() => {
    if (!Capacitor.isNativePlatform()) return;
    import('@capacitor/keyboard')
      .then(({ Keyboard }) => Keyboard.hide())
      .catch(() => {});
  }, []);

  return { keyboardHeight, isKeyboardVisible, hideKeyboard };
}
